import { MentionInputIntermediateStateHandlers, MentionInputProps } from "client/Components";
import {
	getModel,
	setModel,
	TextWithMentions,
	MentionData
} from "client/Components/MentionInput/converters";
import { isProseMirror, MentionInputState, MentionInputStateKind } from "client/Components/MentionInput/types";
import { deepComparison } from "client/Misc/compareUtils";
import { IInputValidation, InputValidationResultKind, IValueValidation } from "client/Validation/input";
import { IValueReducer } from "../reducer";
import { NewValueHandler } from "./misc";

type AttachableMentionInputProps = Pick<
	MentionInputProps,
	| "value"
	| "onChange"
	| "isInvalid"
	| "trackingId"
	| "onBlur"
>;

interface IAttachableMentionInputPropsWithBuilder extends AttachableMentionInputProps {
	readonly validation: IInputValidation | undefined;
	withOnChange(handler: (newValue: MentionInputState) => void): IAttachableMentionInputPropsWithBuilder;
	withOnBlur(handler: (newValue: MentionInputState) => void): IAttachableMentionInputPropsWithBuilder;
}

export class AttachableMentionInputPropsWithBuilder implements IAttachableMentionInputPropsWithBuilder {
	private readonly _extraOnChangeHandlers: NewValueHandler<MentionInputState>[];
	private readonly _extraOnBlurHandlers: NewValueHandler<MentionInputState>[];
	private readonly _reducer: IValueReducer<TextWithMentions>;
	private readonly _intermediateStateHandlers: MentionInputIntermediateStateHandlers;
	private readonly _mentionData: readonly MentionData[];

	public readonly value: MentionInputState;
	public readonly isInvalid: boolean;
	public readonly validation: IInputValidation;
	public readonly trackingId: string;

	constructor(
		reducer: IValueReducer<TextWithMentions>,
		validation: IValueValidation<TextWithMentions>,
		intermediateStateHandlers: MentionInputIntermediateStateHandlers,
		mentionData: readonly MentionData[]
	) {
		this._extraOnBlurHandlers = [];
		this._extraOnChangeHandlers = [];
		this._reducer = reducer;
		this._intermediateStateHandlers = intermediateStateHandlers;
		this._mentionData = mentionData;

		this.value = intermediateStateHandlers.intermediateState;
		this.validation = validation;
		this.isInvalid = this.validation
			? this.validation.getValidationState().kind === InputValidationResultKind.Invalid
			: false;

		this.trackingId = reducer.path;

		this.onChange = this.onChange.bind(this);
		this.onBlur = this.onBlur.bind(this);
		this.withOnChange = this.withOnChange.bind(this);
		this.withOnBlur = this.withOnBlur.bind(this);

		this.updateStateFromValueIfNeeded();
	}

	public onChange(newValue: MentionInputState): void {
		const {
			setIntermediateState
		} = this._intermediateStateHandlers;

		setIntermediateState(newValue);
		this.validation.triggerValueChanged();

		for (const handler of this._extraOnChangeHandlers) {
			handler(newValue);
		}
	}

	public onBlur(newValue: MentionInputState): void {
		if (!isProseMirror(newValue)) {
			throw new Error("This function only works with prosemirror state");
		}

		const {
			setIntermediateState,
			setModelThatRepresentsTheState
		} = this._intermediateStateHandlers;

		const model: TextWithMentions = getModel(newValue.editorState);
		this._reducer.setValue(model);

		setIntermediateState(newValue);
		setModelThatRepresentsTheState(model);

		this.validation.triggerInputFinished();

		for (const handler of this._extraOnBlurHandlers) {
			handler(newValue);
		}
	}

	public withOnChange(handler: (newValue: MentionInputState) => void): IAttachableMentionInputPropsWithBuilder {
		this._extraOnChangeHandlers.push(handler);
		return this;
	}

	public withOnBlur(handler: (newValue: MentionInputState) => void): IAttachableMentionInputPropsWithBuilder {
		this._extraOnBlurHandlers.push(handler);
		return this;
	}

	private updateStateFromValueIfNeeded(): void {
		const {
			getModelThatRepresentsTheState,
			setIntermediateState,
			setModelThatRepresentsTheState
		} = this._intermediateStateHandlers;

		const currentModel: TextWithMentions = this._reducer.value;
		if (deepComparison(currentModel, getModelThatRepresentsTheState())) {
			return;
		}

		setIntermediateState(previousEditorState => {
			if (!isProseMirror(previousEditorState)) {
				throw new Error("This method only support prosemirror state");
			}

			return {
				kind: MentionInputStateKind.ProseMirror,
				editorState: setModel(
					previousEditorState.editorState,
					currentModel,
					this._mentionData,
					false
				)
			};
		});
		setModelThatRepresentsTheState(currentModel);
	}
}

export type IResolveAttachableMentionInputPropsWithBuilder<TValue> = TValue extends TextWithMentions
	? IAttachableMentionInputPropsWithBuilder
	: never;